import { useQuery } from '@tanstack/react-query'
import { releaseApi } from '../api'
import type { Page } from '../components/Layout'

type Signal = 'red' | 'amber' | 'green' | 'unknown'

// ── Signal helpers ────────────────────────────────────────────────────────────

function worst(...signals: Signal[]): Signal {
    const rank: Record<Signal, number> = { unknown: 0, green: 1, amber: 2, red: 3 }
    return signals.reduce((a, b) => (rank[b] > rank[a] ? b : a), 'unknown' as Signal)
}

function signalFromInfraStatus(groups: { dots: { status: string }[] }[] | undefined): Signal {
    if (!groups) return 'unknown'
    for (const g of groups) {
        for (const d of g.dots) {
            if (d.status === 'Error') return 'red'
        }
    }
    for (const g of groups) {
        for (const d of g.dots) {
            if (d.status === 'Warning') return 'amber'
        }
    }
    return 'green'
}

function signalFromBuilds(runs: { state: string; result: string | null }[] | undefined): Signal {
    if (!runs) return 'unknown'
    // AB#2155 — an empty run list means the deployable→pipeline map is unconfigured (or every fetch
    // failed silently), not that everything is green. Never render "all green" from zero signals.
    if (runs.length === 0) return 'unknown'
    if (runs.some(r => r.result === 'failed')) return 'red'
    if (runs.some(r => r.state === 'completed' && r.result !== 'succeeded' && r.result !== null)) return 'amber'
    return 'green'
}

function signalFromMigrations(pending: string[] | undefined): Signal {
    if (pending === undefined) return 'unknown'
    return pending.length > 0 ? 'amber' : 'green'
}

function signalFromCluster(
    externalSecrets: { ready: boolean }[] | undefined,
    argoCdApps: { syncStatus: string; healthStatus: string }[] | undefined,
): Signal {
    if (!externalSecrets || !argoCdApps) return 'unknown'
    if (externalSecrets.some(s => !s.ready)) return 'red'
    if (argoCdApps.some(a => a.healthStatus === 'Degraded' || a.syncStatus === 'OutOfSync')) {
        // OutOfSync is amber, Degraded is red
        if (argoCdApps.some(a => a.healthStatus === 'Degraded')) return 'red'
        return 'amber'
    }
    return 'green'
}

// ── Traffic-light dot ─────────────────────────────────────────────────────────

function TrafficDot({ signal, size = 'lg' }: { signal: Signal; size?: 'sm' | 'lg' }) {
    const colours: Record<Signal, string> = {
        green:   'bg-emerald-500 shadow-emerald-500/60',
        amber:   'bg-amber-400 shadow-amber-400/60',
        red:     'bg-red-500 shadow-red-500/60',
        unknown: 'bg-slate-600',
    }
    const sizeClass = size === 'lg' ? 'w-6 h-6' : 'w-3.5 h-3.5'
    const shadowClass = signal !== 'unknown' ? 'shadow-[0_0_10px_2px]' : ''
    return (
        <span
            className={[
                'inline-block rounded-full shrink-0',
                sizeClass,
                colours[signal],
                shadowClass,
            ].join(' ')}
        />
    )
}

// ── Source card ───────────────────────────────────────────────────────────────

function SourceCard({
    title,
    signal,
    detail,
    onNavigate,
    targetPage,
}: {
    title: string
    signal: Signal
    detail: string
    onNavigate: (page: Page) => void
    targetPage: Page
}) {
    return (
        <button
            type="button"
            onClick={() => onNavigate(targetPage)}
            className="flex items-start gap-4 p-5 bg-slate-800/60 border border-slate-700 rounded-xl hover:bg-slate-800 hover:border-slate-600 transition-colors text-left w-full group"
        >
            <TrafficDot signal={signal} />
            <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-200 group-hover:text-white transition-colors">
                    {title}
                </p>
                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{detail}</p>
            </div>
            <svg
                className="w-4 h-4 text-slate-600 group-hover:text-slate-400 shrink-0 mt-0.5 transition-colors"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
            >
                <path d="M6 3l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        </button>
    )
}

// ── Overall signal ────────────────────────────────────────────────────────────

function OverallSignal({ signal }: { signal: Signal }) {
    const ring: Record<Signal, string> = {
        green:   'ring-emerald-500/40',
        amber:   'ring-amber-400/40',
        red:     'ring-red-500/40',
        unknown: 'ring-slate-700',
    }
    const glow: Record<Signal, string> = {
        green:   'shadow-emerald-500/30',
        amber:   'shadow-amber-400/30',
        red:     'shadow-red-500/30',
        unknown: '',
    }
    const bg: Record<Signal, string> = {
        green:   'bg-emerald-500',
        amber:   'bg-amber-400',
        red:     'bg-red-500',
        unknown: 'bg-slate-700',
    }
    const label: Record<Signal, string> = {
        green:   'All systems healthy',
        amber:   'Warnings detected',
        red:     'Errors detected',
        unknown: 'Status unknown',
    }

    return (
        <div className="flex flex-col items-center gap-4">
            <div
                className={[
                    'w-28 h-28 rounded-full ring-8 flex items-center justify-center',
                    bg[signal],
                    ring[signal],
                    signal !== 'unknown' ? `shadow-[0_0_48px_8px] ${glow[signal]}` : '',
                ].join(' ')}
            />
            <p className="text-lg font-semibold text-slate-200">{label[signal]}</p>
        </div>
    )
}

// ── Page ──────────────────────────────────────────────────────────────────────

interface OverviewPageProps {
    onNavigate: (page: Page) => void
}

export function OverviewPage({ onNavigate }: OverviewPageProps) {
    const infraQuery = useQuery({
        queryKey: ['infraStatus'],
        queryFn: releaseApi.getInfraStatus,
        refetchInterval: 30_000,
    })
    const buildsQuery = useQuery({
        queryKey: ['pipelineRuns'],
        queryFn: releaseApi.getPipelineRuns,
        refetchInterval: 30_000,
    })
    const migrationsQuery = useQuery({
        queryKey: ['migrations'],
        queryFn: releaseApi.getMigrations,
        refetchInterval: 30_000,
    })
    const clusterQuery = useQuery({
        queryKey: ['clusterHealth'],
        queryFn: releaseApi.getClusterHealth,
        refetchInterval: 30_000,
    })

    const infraSignal  = signalFromInfraStatus(infraQuery.data?.groups)
    const buildsSignal = signalFromBuilds(buildsQuery.data?.runs)
    const migrSignal   = signalFromMigrations(migrationsQuery.data?.pending)
    const clusterSignal = signalFromCluster(
        clusterQuery.data?.externalSecrets,
        clusterQuery.data?.argoCdApps,
    )

    const overall = worst(infraSignal, buildsSignal, migrSignal, clusterSignal)

    const buildsDetail = (() => {
        const runs = buildsQuery.data?.runs ?? []
        if (buildsQuery.isLoading) return 'Loading…'
        if (buildsQuery.isError)   return 'Failed to fetch'
        // AB#2155 — zero runs = the Ado:PipelineIds map is unconfigured; saying "all green" here
        // masked a dead signal source.
        if (runs.length === 0) return 'No deployables configured'
        const failed = runs.filter(r => r.result === 'failed')
        if (failed.length > 0) return `${failed.length} failed run${failed.length > 1 ? 's' : ''}`
        return `${runs.length} deployable${runs.length !== 1 ? 's' : ''} — all green`
    })()

    const migrDetail = (() => {
        if (migrationsQuery.isLoading) return 'Loading…'
        if (migrationsQuery.isError)   return 'Failed to fetch'
        const pending = migrationsQuery.data?.pending.length ?? 0
        return pending > 0 ? `${pending} pending migration${pending > 1 ? 's' : ''}` : 'All migrations applied'
    })()

    const clusterDetail = (() => {
        if (clusterQuery.isLoading) return 'Loading…'
        if (clusterQuery.isError)   return 'Failed to fetch'
        const eso = clusterQuery.data?.externalSecrets ?? []
        const argocd = clusterQuery.data?.argoCdApps ?? []
        const badEso = eso.filter(s => !s.ready).length
        const outOfSync = argocd.filter(a => a.syncStatus === 'OutOfSync').length
        const degraded = argocd.filter(a => a.healthStatus === 'Degraded').length
        if (badEso || degraded || outOfSync) {
            const parts = []
            if (degraded) parts.push(`${degraded} degraded`)
            if (outOfSync) parts.push(`${outOfSync} out-of-sync`)
            if (badEso) parts.push(`${badEso} ESO not ready`)
            return parts.join(' · ')
        }
        return `${argocd.length} ArgoCD apps in sync`
    })()

    const infraDetail = (() => {
        if (infraQuery.isLoading) return 'Loading…'
        if (infraQuery.isError)   return 'Failed to fetch'
        const groups = infraQuery.data?.groups ?? []
        const errors = groups.flatMap(g => g.dots).filter(d => d.status === 'Error').length
        const warnings = groups.flatMap(g => g.dots).filter(d => d.status === 'Warning').length
        if (errors) return `${errors} error${errors > 1 ? 's' : ''}`
        if (warnings) return `${warnings} warning${warnings > 1 ? 's' : ''}`
        return 'All 5 swimlanes healthy'
    })()

    const allLoading =
        infraQuery.isLoading && buildsQuery.isLoading &&
        migrationsQuery.isLoading && clusterQuery.isLoading

    const fetchedAt = [
        infraQuery.data?.fetchedAt,
        buildsQuery.data?.fetchedAt,
        migrationsQuery.data?.fetchedAt,
        clusterQuery.data?.fetchedAt,
    ].filter(Boolean).sort().pop()

    return (
        <div className="flex-1 flex flex-col">
            {/* Header */}
            <div className="px-4 sm:px-8 pt-8 pb-6 border-b border-slate-800">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-xl font-semibold text-white tracking-tight">Overview</h1>
                        <p className="text-sm text-slate-500 mt-0.5">
                            Aggregated health across all monitoring sources
                        </p>
                    </div>
                    {fetchedAt && (
                        <p className="text-xs text-slate-600">
                            Last updated {new Date(fetchedAt).toLocaleTimeString()}
                        </p>
                    )}
                </div>
            </div>

            <div className="flex-1 px-4 sm:px-8 py-8 space-y-10">
                {/* Overall traffic light */}
                <div className="flex justify-center py-4">
                    {allLoading
                        ? <div className="w-28 h-28 rounded-full bg-slate-800 animate-pulse" />
                        : <OverallSignal signal={overall} />
                    }
                </div>

                {/* Source cards */}
                <div>
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-3">
                        Signal sources — click to navigate
                    </p>
                    <div className="grid grid-cols-1 gap-3">
                        <SourceCard
                            title="Infra Status"
                            signal={infraSignal}
                            detail={infraDetail}
                            onNavigate={onNavigate}
                            targetPage="status"
                        />
                        <SourceCard
                            title="CI / CD Builds"
                            signal={buildsSignal}
                            detail={buildsDetail}
                            onNavigate={onNavigate}
                            targetPage="pipeline"
                        />
                        <SourceCard
                            title="EF Migrations"
                            signal={migrSignal}
                            detail={migrDetail}
                            onNavigate={onNavigate}
                            targetPage="troubleshoot"
                        />
                        <SourceCard
                            title="Cluster Health"
                            signal={clusterSignal}
                            detail={clusterDetail}
                            onNavigate={onNavigate}
                            targetPage="gitops"
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}
